/* eslint-disable @typescript-eslint/no-explicit-any */
// Vendor
import type { Ref } from 'vue';
import { ref } from 'vue';
import { Router } from 'vue-router';

// Stores
import { useUiStore } from '@stores/ui';

// Personal libs
import { logger } from '@shared/lib/logger/logger-manager';
import bus from '@shared/lib/eventbus.lib';
import { BusEventSessionEnum } from '@shared/enum/bus/bus-events-session.enum';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { IAuthResponse } from '@shared/interface/auth-response.interface';
import authRepository from 'src/data/repository/auth.repository';
import { ApiErrorLabel } from '@shared/enum/api-error-label.enum';
import {
  SessionInteractionEnum,
  SessionStatusEnum,
} from '@shared/enum/session-manager.enum';
import { IUser } from '@shared/interface/model.interface';
import { AccessToken } from './access-token.class';
import { RefreshToken } from './refresh-token.class';
import { ApiStatusEnum } from '@shared/enum/api-enum';
import { ApiError } from '@shared/error/api.error';
import { $t } from '@i18n/utils';
import { IAuthRequest } from '@shared/interface/auth-request.interface';
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import { ILogginActionsParam, loginActions } from '@shared/lib/login.lib';
import { TokenStatusEnum } from '@shared/enum/token-status.enum';
import { RoleEnum } from '@shared/enum/role.enum';

/**
 * Session Manager
 */
class SessionManager {
  /**
   * Public static properties
   */
  public accessToken: AccessToken;
  public refreshToken: RefreshToken;

  /**
   * Public reactive properties
   */
  public currentUser: Ref<IUser | undefined>;
  public isOnline: Ref<boolean | undefined>;
  public sessionStatus: Ref<SessionStatusEnum | undefined>;
  public sessionInteractionStatus: Ref<SessionInteractionEnum | undefined>;

  /**
   * Private properties
   */
  private debug?: boolean;

  /**
   * Constructor
   */
  public constructor() {
    // Init properties
    this.accessToken = new AccessToken();
    this.refreshToken = new RefreshToken();

    // Init reactive properties
    this.currentUser = ref<IUser | undefined>(undefined);
    // this.isOnline = ref<boolean | undefined>(undefined);
    this.isOnline = ref<boolean | undefined>(true); // TODO delete this line
    this.sessionStatus = ref<SessionStatusEnum | undefined>(
      SessionStatusEnum.NOT_LOGGED,
    );
    this.sessionInteractionStatus = ref<SessionInteractionEnum | undefined>(
      undefined,
    );

    this.debug = false;
    this.debug && this.log('Inside constructor');
  }

  /**
   * ***********************************************************************************************
   * Public methods : testers
   * ***********************************************************************************************
   */
  public isLoggedInOnline(): boolean {
    return this.sessionStatus.value === SessionStatusEnum.LOGGED_IN_ONLINE;
  }

  public isLoggedInOffline(): boolean {
    return this.sessionStatus.value === SessionStatusEnum.LOGGED_IN_OFFLINE;
  }

  public isLoggedOut(): boolean {
    return this.sessionStatus.value === SessionStatusEnum.LOGGED_OUT;
  }

  public log(message: string) {
    logger?.info(`[SessionManager] - ${message}`);
  }

  /**
   * True if : sessionStatus != SessionStatusEnum.LOGGING_OUT && refreshToken.getTokenStatus() !== TokenStatusEnum.DEAD
   * @returns boolean
   */
  public isActive(): boolean {
    return (
      this.sessionStatus.value !== SessionStatusEnum.LOGGING_OUT &&
      this.refreshToken.getTokenStatus() !== TokenStatusEnum.INACTIVE
    );
  }

  /**
   * ***********************************************************************************************
   * Public methods : setters
   * ***********************************************************************************************
   */
  public async initTokens() {
    this.debug && this.log('Inside initTokens');
    this.accessToken.refreshTokenData().then(() => this.accessToken.refreshStatus());
    this.refreshToken.refreshTokenData().then(() => this.refreshToken.refreshStatus());
  }

  public async refreshStatus(activeUser?: IUser) {
    const user = activeUser ?? await dbGetActiveUser()
    const loggingIn = this.sessionStatus.value ? (this.sessionStatus.value ?? '') === SessionStatusEnum.LOGGING_IN : false
    const loggingOut = this.sessionStatus.value ? (this.sessionStatus.value ?? '') === SessionStatusEnum.LOGGING_OUT : false
    if (user?.isConnected === true && !loggingIn && !loggingOut) {
      this.setStatusLoggedIn()
    } else {
      this.setStatusLoggedOut()
    }
  }

  public setStatusLoggedIn() {
    this.debug && this.log('Inside setStatusLoggedIn');
    if (this.isOnline.value) {
      this.setStatusLoggedInOnLine();
    } else {
      this.setStatusLoggedInOffLine();
    }
  }

  public setStatusLoggedInOnLine() {
    this.debug && this.log('Inside setStatusLoggedInOnLine');
    this.sessionStatus.value = SessionStatusEnum.LOGGED_IN_ONLINE;
  }

  public setStatusLoggedInOffLine() {
    this.debug && this.log('Inside setStatusLoggedInOffLine');
    this.sessionStatus.value = SessionStatusEnum.LOGGED_IN_OFFLINE;
  }

  public setStatusLoggingIn() {
    this.debug && this.log('Inside setStatusLoggingIn');
    this.sessionStatus.value = SessionStatusEnum.LOGGING_IN;
  }

  public setStatusLoggingOut() {
    this.debug && this.log('Inside setStatusLoggingOut');
    this.sessionStatus.value = SessionStatusEnum.LOGGING_OUT;
  }

  public setStatusLoggedOut() {
    this.debug && this.log('Inside setStatusLoggout');
    this.sessionStatus.value = SessionStatusEnum.LOGGED_OUT;
    this.sessionInteractionStatus.value = undefined;
  }

  /**
   * ***********************************************************************************************
   * Public methods : refresh token state management
   * ***********************************************************************************************
   */
  public askBeforeEnd() {
    this.debug && this.log('Inside askBeforeEnd');

    this.sessionStatus.value = SessionStatusEnum.PENDING;
    this.sessionInteractionStatus.value =
      SessionInteractionEnum.ASKING_BEFORE_END;

    bus.emit(BusEventSessionEnum.ASKING_BEFORE_END);
  }

  public askReconnect() {
    this.debug && this.log('Inside askReconnect');
    this.sessionStatus.value = SessionStatusEnum.PENDING;
    this.sessionInteractionStatus.value =
      SessionInteractionEnum.ASKING_RECONNECT;

    bus.emit(BusEventSessionEnum.ASKING_RECONNECT);
  }

  public askSessionExpired(ttl?: number) {
    this.debug && this.log('Inside askSessionExpired');
    this.sessionStatus.value = SessionStatusEnum.PENDING;
    this.sessionInteractionStatus.value = SessionInteractionEnum.ASKING_SESSION_EXPIRED;

    bus.emit(BusEventSessionEnum.ASKING_SESSION_EXPIRED, ttl);
  }

  /**
   * ***********************************************************************************************
   * Public methods : update user
   * ***********************************************************************************************
   */
  public updateUserRoles(roles?: RoleEnum[]){
    if (roles) {
      sessionManager.currentUser.value = {
        ...sessionManager.currentUser.value,
        roles: roles
      }
    }
  }

  /**
   * ***********************************************************************************************
   * Public methods : login / logout management
   * ***********************************************************************************************
   */

  /**
   * setCurrentUser
   * @param params ILogginActionsParam | undefined
   * @returns Promise<IUser | undefined>
   */
  public async setCurrentUser(
    params?: ILogginActionsParam,
  ): Promise<IUser | undefined> {
    // Get active user
    const activeUser = await dbGetActiveUser();

    const response =
      params?.apiResponse?.returnSuccess ?? params?.authResponseDto;
    if (response != null) {
      const providedUser = response?.user;
      if (activeUser) {
        Object.assign(activeUser, providedUser);
        this.currentUser.value = activeUser;
      } else if (providedUser != null) {
        this.currentUser.value = providedUser;
      } else {
        this.currentUser.value = undefined;
      }
    } else if (activeUser != null) {
      this.currentUser.value = activeUser;
    }

    return this.currentUser.value;
  }

  public loginFailure(apiErrorLabel: ApiErrorLabel) {
    this.debug && this.log('Inside loginFailure');
  }

  public loginSuccess() {
    this.debug && this.log('Inside loginSuccess');
    this.setStatusLoggedIn();
    this.sessionInteractionStatus.value = undefined
  }

  /**
   * Login :
   * - update remainingTime for both tokens
   * - set session status to logged in (online or offline)
   */
  public async login() {
    await this.initTokens();

    this.loginSuccess();
  }

  /**
   * Logout :
   * - clear cookies
   * - clear refresh token
   */
  public logout() {
    this.debug && this.log('Inside logout');
    // this.error = undefined;
    this.currentUser.value = undefined;

    // Clear cookies (accessToken)
    this.accessToken.clearToken();

    // Clear refresh token
    this.refreshToken.clearToken();

    // Reset session status
    this.setStatusLoggedOut();
  }

  /**
   * auth
   * @param params IAuthRequest
   * @returns Promise<AuthResponseDto | undefined>
   */
  public async auth(params: IAuthRequest): Promise<IAuthResponse | undefined> {
    this.debug && this.log('Inside auth');
    this.setStatusLoggingIn();

    return authRepository
      .auth(params)
      .then(
        async (response: IApiResponse<IAuthResponse, string> | undefined) => {
          const returnSuccess = await this.manageAuthResponse(response);

          if (!returnSuccess) {
            throw new ApiError(
              $t('signin.login.error.AUTH_DATABASE_ERR_01'),
              'AUTH_DATABASE_ERR_01',
            );
          } else {
            return returnSuccess;
          }
        },
      );
  }

  /**
   * ***********************************************************************************************
   * Public methods : manage access and refresh tokens life
   * ***********************************************************************************************
   */

  /**
   * Used by the APIs : check if the session is active
   * Refreshes the access token if it is not active
   * @param debug boolean | undefined
   * @returns Promise<boolean>
   */
  public async checkAccessTokenValidity(): Promise<boolean> {
    if (!(await this.accessToken.isValid()) && this.refreshToken.getTokenStatus() !== TokenStatusEnum.INACTIVE) {
      return await this.accessToken
        .refresh()
        .then(async (response) => this.manageAuthResponse(response) != null)
        .catch((error: Error) => {
          // TODO : traiter le cas d'une erreur ApiErrorLabel.NetworkError => session active : afficher hors connexion ou serveur hors ligne
          sessionManager.log('checkApiValidity - error : ' + error.message);
          return true;
        });
    } else {
      return true;
    }
  }

  /**
   * Used by the CRONJOB : manage refresh token status
   */
  public checkRefreshTokenValidity = async (): Promise<void> => {
    const activeUser = await dbGetActiveUser();

    await this.refreshToken.refreshTokenData(activeUser);
    await this.refreshToken.refreshStatus();
    await this.refreshStatus(activeUser);  

    switch (this.refreshToken.getTokenStatus()) {
      case TokenStatusEnum.ACTIVE:
        break;
      case TokenStatusEnum.END_OF_LIVE:
        if (
          activeUser?.isConnected === true &&
          this.isOnline.value && 
          (
            this.sessionInteractionStatus.value == null ||
            (
              this.sessionInteractionStatus.value != SessionInteractionEnum.ASKING_BEFORE_END &&
              this.sessionInteractionStatus.value != SessionInteractionEnum.ASKING_RECONNECT
            )
          )
        ) {
          this.askBeforeEnd();
        }
        break;
      case TokenStatusEnum.INACTIVE:
        if (
          activeUser?.isConnected === true &&
          this.isOnline.value && 
          this.refreshToken.getTtl() != null &&
          (this.sessionInteractionStatus.value == null ||
            this.sessionInteractionStatus.value ==
              SessionInteractionEnum.ASKING_BEFORE_END)
        ) {
          this.askSessionExpired(this.refreshToken.getTtl());
        }
        break;
      default:
        break;
    }
  };

  /**
   * ***********************************************************************************************
   * Private methods
   * ***********************************************************************************************
   */
  /**
   * manageAuthResponse :
   * - logout if response is null
   * - if response code is AUTH_OK or REFRESH_OK : loginActions() and sessionManager.login()
   * - else : logout()
   * - TODO : manage correctly specifics errors like NetworkError
   * @param response IApiResponse<AuthResponseDto, string> | undefined
   * @returns AuthResponseDto | undefined
   */
  private async manageAuthResponse(
    response: IApiResponse<IAuthResponse, string> | undefined,
  ): Promise<IAuthResponse | undefined> {
    if (!response) {
      this.logout();

      return undefined;
    } else {
      if (
        ['REFRESH_OK', 'AUTH_OK'].includes(response.returnCode) &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        await loginActions({
          displayLoader: true,
          authResponseDto: response.returnSuccess,
          hasToGetUserSettings: true,
        });

        // Refresh tokens
        await sessionManager.login();
      } else if (
        response.returnStatus === ApiStatusEnum.ERROR &&
        response.returnCode === ApiErrorLabel.NetworkError
      ) {
        // TODO : gérer correctement les erreurs en gérant un message d'erreur à afficher
        this.loginFailure(ApiErrorLabel.NetworkError);
      } else {
        this.logout();
      }

      return response.returnSuccess;
    }
  }
}

export const sessionManager = new SessionManager();
